/**
 * Active-account context for the admin SPA. Persists the selection in
 * localStorage so a refresh keeps you in the same account, and broadcasts
 * the value via a header that `api.ts` injects on every request.
 *
 * The backend reads `X-Account-Id` in dev and the corresponding Auth0
 * claim in production, so this single setting drives all server-side
 * scoping. Set it once, every list endpoint filters automatically.
 */

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { api, setAccountIdGetter } from "../../lib/api";

const STORAGE_KEY = "engage_account_id";
const DEFAULT_ACCOUNT = "paybyrd-system";

export interface Account {
  id: string;
  display_name: string;
  kind: string;
  partner_count: number;
  voucher_count: number;
}

interface AccountContextValue {
  accountId: string;
  accounts: Account[];
  loading: boolean;
  setAccountId: (id: string) => void;
  refetch: () => void;
}

const Ctx = createContext<AccountContextValue>({
  accountId: DEFAULT_ACCOUNT,
  accounts: [],
  loading: false,
  setAccountId: () => {},
  refetch: () => {},
});

export function AccountProvider({ children }: { children: ReactNode }) {
  const [accountId, setAccountIdState] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_ACCOUNT;
  });
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  // Wire api.ts so every request carries X-Account-Id. The getter reads a
  // ref (updated synchronously on every render) rather than closing over
  // `accountId`, so it always returns the current account regardless of
  // effect ordering — a list's refetch effect can fire before this provider's
  // effects and still send the correct header.
  const accountIdRef = useRef(accountId);
  accountIdRef.current = accountId;
  useEffect(() => {
    setAccountIdGetter(() => accountIdRef.current);
  }, []);

  const setAccountId = useCallback((id: string) => {
    localStorage.setItem(STORAGE_KEY, id);
    setAccountIdState(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<{ data: Account[] }>("/engage/accounts")
      .then((res) => {
        if (cancelled) return;
        setAccounts(res.data);
        // If the saved account no longer exists (deleted, renamed),
        // fall back to the default — keeps the SPA from sending an
        // X-Account-Id that the backend won't recognize.
        if (!res.data.some((a) => a.id === accountId)) {
          setAccountId(DEFAULT_ACCOUNT);
        }
      })
      .catch(() => {
        // Account listing might not exist on a fresh deploy; degrade to
        // the default rather than blocking the SPA from rendering.
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [tick, accountId, setAccountId]);

  return (
    <Ctx.Provider
      value={{
        accountId,
        accounts,
        loading,
        setAccountId,
        refetch: () => setTick((t) => t + 1),
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useAccount(): AccountContextValue {
  return useContext(Ctx);
}
